import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Button } from "@mui/material";
import { createContext, MutableRefObject, RefObject } from "react";
import screenfull from "screenfull";
import { IdTagLogo } from "src/assets/icons/id-tag-logo";
import { TagMetaData } from "src/config/store/services/media-api/types";
import { useIsMobileView } from "../../hooks/use-is-mobile-view";
import { LoadingPlaceholder } from "../loading-placeholder/loading-placeholder";
import { classes } from "./media-player.styles";
import ShareButton from "./share-button/share-button";
import TagsDrawer from "./tags-drawer/tags-drawer";
import { useMediaPlayer } from "./use-media-player";
import VideoControls from "./video-controls/video-controls";
import VideoTag from "./video-tag/video-tag";

type MediaPlayerProps = {
  url?: string;
  tagsData: TagMetaData[];
  type: MediaType;
  variant?: "modal" | "static";
  isLoading?: boolean;
};

export type MediaType = "video" | "photo";

type VideoPlayerContext = {
  isPlaying: boolean;
  videoElement: HTMLVideoElement;
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
  handleTags: () => void;
  togglePlay: () => void;
  wrapperRef: MutableRefObject<HTMLDivElement>;
  toggleFullScreen: () => void;
  toggleTags: () => void;
  showTags: boolean;
};

const drawerWidth = 300;

export const PlayerContext = createContext({} as VideoPlayerContext);

export const MediaPlayer = ({
  url,
  tagsData,
  type,
  variant = "static",
  isLoading,
}: MediaPlayerProps) => {
  const isMobile = useIsMobileView();

  const {
    isPlaying,
    mediaRef,
    videoControls,
    wrapperRef,
    metaDataLoaded,
    drawerOpen,
    showTags,
    showControls,
    videoOrientation,
    setIsPlaying,
    setMetadataLoaded,
    handleTags,
    togglePlay,
    toggleFullScreen,
    toggleDrawer,
    toggleTags,
    handleShowControls,
    setTagRefs,
  } = useMediaPlayer(tagsData, type);

  return (
    <PlayerContext.Provider
      value={
        type === "video"
          ? {
              isPlaying: isPlaying,
              videoElement: mediaRef.current as HTMLVideoElement,
              wrapperRef,
              setIsPlaying,
              handleTags,
              togglePlay,
              toggleFullScreen,
              toggleTags,
              showTags,
            }
          : ({ wrapperRef, handleTags } as VideoPlayerContext)
      }
    >
      {(!metaDataLoaded || isLoading) && <LoadingPlaceholder />}

      <Box
        ref={wrapperRef}
        id="testWrapper"
        sx={{
          height: "100%",
          position: "relative",
        }}
      >
        <Box
          sx={{
            ...classes.videoWrapper,
            marginRight:
              variant === "static" && drawerOpen ? `${drawerWidth}px` : 0,
            visibility: metaDataLoaded && !isLoading ? "initial" : "hidden",
            opacity: metaDataLoaded && !isLoading ? "1" : "0",
            cursor: showControls ? "default" : "none",
            ".controls-wrapper":
              !isPlaying || showControls
                ? {
                    opacity: 1,
                    transform: "translateY(0%)",
                  }
                : {},
          }}
          onMouseLeave={() => {
            if (isPlaying) {
              handleShowControls(false);
            }
          }}
          onMouseMove={() => {
            handleShowControls(true);
          }}
        >
          <Box sx={classes.additionalActions}>
            <ShareButton />
            {!isMobile && (
              <Button
                sx={classes.drawerButton}
                variant="contained"
                onClick={toggleDrawer}
                endIcon={
                  drawerOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />
                }
              >
                {drawerOpen ? "Hide" : "Show"} tags list
              </Button>
            )}
          </Box>

          <Box sx={classes.overlay} className="overlay" onClick={togglePlay}>
            {!isMobile && (
              <Box
                sx={classes.overlay}
                className="fullScreenOverlay"
                onDoubleClick={toggleFullScreen}
              />
            )}
          </Box>

          <Box
            className="tagsWrapper"
            sx={{
              height: "100%",
              width:
                isMobile && document.fullscreenElement
                  ? "initial"
                  : "fit-content",
              margin: "auto",
              overflow: "hidden",
              position: "relative",
            }}
          >
            {showTags &&
              tagsData.map((el, i) =>
                el.data ? (
                  <VideoTag
                    tag={el as Required<TagMetaData>}
                    key={el.id}
                    ref={(node: HTMLDivElement) => setTagRefs(node, i)}
                  />
                ) : null
              )}

            <Box sx={classes.watermark}>
              <IdTagLogo />
            </Box>
            {type === "video" && (
              <>
                <video
                  preload="auto"
                  playsInline
                  style={{
                    height: "100%",
                    width:
                      videoOrientation() === "horizontal" &&
                      !screenfull.isFullscreen
                        ? "100%"
                        : "initial",
                  }}
                  ref={mediaRef as RefObject<HTMLVideoElement>}
                  onLoadedMetadata={() => {
                    setMetadataLoaded(true);
                  }}
                  onEnded={() => {
                    videoControls.current?.handleOnTimeUpdate();
                    setIsPlaying(false);
                  }}
                  onProgress={() => {
                    videoControls.current?.handleBuffer();
                  }}
                >
                  <source src={url}></source>
                </video>
              </>
            )}
            {type === "photo" && (
              <img
                ref={mediaRef as RefObject<HTMLImageElement>}
                style={{
                  height: "100%",
                }}
                src={url}
                alt="tag"
                onLoad={() => {
                  setMetadataLoaded(true);
                }}
              />
            )}
          </Box>
          {type === "video" && mediaRef?.current && metaDataLoaded && (
            <VideoControls ref={videoControls} idTags={tagsData} />
          )}
        </Box>
        {!isMobile && (
          <TagsDrawer
            open={drawerOpen}
            tags={tagsData || []}
            drawerWidth={drawerWidth}
          />
        )}
      </Box>
    </PlayerContext.Provider>
  );
};

export default MediaPlayer;
